import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect
} from "react";
import PropTypes from "kf-proptypes/widgets";
import { Icon } from "@widgets/common/components/primitives/icon";

import { OverflowText } from "@widgets/datadisplay/index.jsx";
import {
  Tooltip,
  TOOLTIP_POSITION,
  TOOLTIP_TYPES
} from "../../feedback/tooltip/index.jsx";
import { InvertedInfoIcon } from "../../icons/index.jsx";
import { focusAtEnd } from "../../common/libs/util.js";

import styles from "./editable.input.css";

const NO_OP = () => {};

export const EDITABLES_SIZE = {
  MEDIUM: "medium",
  SMALL: "small"
};

export const EditableInput = forwardRef((props, ref) => {
  const {
    value,
    onChange = NO_OP,
    onEdit,
    onKeyPress,
    onKeyDown,
    onEnter,
    onSave,
    onCancel,
    className,
    multiline = false,
    readOnly = false,
    isError = false,
    size = EDITABLES_SIZE.MEDIUM,
    error = "",
    errorTooltipPosition = TOOLTIP_POSITION.TOP,
    ...restProps
  } = props;

  //   const [currentValue, setCurrentValue] = useState(null);
  const inputRef = useRef(null);

  let cancelled = false;

  useEffect(function listenToKeyup() {
    if (!readOnly) inputRef.current.addEventListener("keyup", (e) => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function clearInput() {
      if (readOnly) return;
      if (value) {
        inputRef.current.innerText = value;
      } else {
        inputRef.current.innerText = "";
      }
    },
    [readOnly, value]
  );

  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        inputRef.current.setAttribute("contenteditable", true);
        inputRef.current.focus();
        focusAtEnd(inputRef.current);
        // inputRef.current.click();
      },
      blur: () => {
        inputRef.current.blur();
      },
      setValue: (value) => {
        inputRef.current.innerText = value;
      },
      select: () => {
        inputRef.current.setAttribute("contenteditable", true);
        window.getSelection().selectAllChildren(inputRef.current);
      }
    };
  });

  const handleOnClick = (e) => {
    inputRef.current.setAttribute("contenteditable", true);
    inputRef.current.focus();
  };

  const handleOnChange = (e) => {
    onChange(inputRef.current.innerText);
  };

  const handleOnFocus = (e) => {
    onEdit && onEdit();
  };

  const handleOnBlur = (e) => {
    e.stopPropagation();
    inputRef.current.setAttribute("contenteditable", false);
    if (!cancelled) {
      onSave && onSave(inputRef.current.innerText, e);
      inputRef.current.scrollLeft = 0;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Escape") {
      cancelled = true;
      onCancel();
    } else if (e.key === "Enter") {
      onEnter && onEnter(inputRef.current.textContent, e);
      if (!multiline) {
        cancelled = false;
        inputRef.current.blur();
      }
    }
    onKeyPress && onKeyPress(e);
  };

  const handlePaste = (event) => {
    let pastedContent = (event.clipboardData || window.clipboardData).getData(
      "text"
    );
    const selection = window.getSelection();
    if (!selection.rangeCount) return false;
    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(pastedContent));
    onChange(inputRef.current.innerText);
    event.preventDefault();
  };

  return readOnly ? (
    <OverflowText className={className}>{value}</OverflowText>
  ) : (
    <Tooltip
      content={
        error ? (
          <div className={styles.tooltipError}>
            <Icon className={styles.invertedInfoIcon}>
              <InvertedInfoIcon />
            </Icon>
            <span>{error}</span>
          </div>
        ) : (
          ""
        )
      }
      position={errorTooltipPosition}
      visible={!!error}
      type={TOOLTIP_TYPES.SMALL}
    >
      <div
        type="text"
        className={`${multiline ? styles.multiline : styles.singleline} ${
          styles[size]
        } ${className} ${isError && styles.errorStyle}`}
        onClick={handleOnClick}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onInput={handleOnChange}
        onKeyDown={onKeyDown}
        onKeyPress={handleKeyPress}
        onPaste={handlePaste}
        ref={inputRef}
        data-component="editableinput"
        {...restProps}
      />
    </Tooltip>
  );
});

EditableInput.propTypes = {
  /**
   * Value which user enters to change existing value
   */
  value: PropTypes.string,
  /**
   * This will enables whether editable will be single or multiline
   */
  multiline: PropTypes.bool,
  /**
   * onEdit function will trigger when user trying to edit
   */
  onEdit: PropTypes.func,
  /**
   * onSave will trigger when user moves onFocus
   */
  onSave: PropTypes.func.isRequired,
  /**
   * onCancel will triger when remove
   */
  onCancel: PropTypes.func,
  /**
   * onChange will trigger when value changes
   */
  onChange: PropTypes.func.isRequired,
  /**
   * className will changes the ui
   */
  className: PropTypes.string,
  /**
   * onKeyPress will trigger when user clicks any keyboard
   */
  onKeyPress: PropTypes.func,
  /**
   * onKeyDown  will trigger when user clicks any keyboard
   */
  onKeyDown: PropTypes.func,
  /**
   * onEnter  will trigger when user clicks any keyboard
   */
  onEnter: PropTypes.func,
  /**
   * readOnly enables user is not able to edit field
   */
  readOnly: PropTypes.bool,
  /**
   * isError will trigger whether if there is an error
   */
  isError: PropTypes.bool,
  /**
   * error will trigger whether if there is an error
   */
  error: PropTypes.string,
  /**
   * position of the error tooltip
   */
  errorTooltipPosition: PropTypes.oneOf(Object.values(TOOLTIP_POSITION)),
  /**
   * Different variantion in the height of the component
   */
  size: PropTypes.oneOf(Object.values(EDITABLES_SIZE))
};
